/**
 * displayPrefs — Single source of truth for user-facing format preferences.
 *
 * Why this exists: useFormatters (React hook) can't be called from plain
 * helpers like api.js's `formatUsd`. We need a non-React module that both
 * sides can read so a currency switch in Settings → Language & Region
 * propagates to every formatter on the page, not just hook-using ones.
 *
 * Lifecycle:
 *   - On import: read localStorage for currency/number/timezone
 *   - On import: listen for localStorage `storage` events AND in-tab
 *     CustomEvents dispatched by the Settings panel (`haythix:currency`,
 *     `haythix:number-format`, `haythix:timezone`).
 *   - useCurrency() hook calls `setRates()` when FX rates load so this
 *     module always has the latest conversion table.
 */

const SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', INR: '₹',
  BRL: 'R$', AUD: 'A$', CAD: 'C$', CHF: 'CHF', KRW: '₩',
  MXN: 'MX$', SGD: 'S$', HKD: 'HK$', TRY: '₺', ZAR: 'R',
};

const NUMBER_LOCALES = {
  auto:    undefined,
  'en-US': 'en-US',
  'en-EU': 'de-DE',
  'en-IN': 'en-IN',
  'en-PK': 'en-PK',
};

const state = {
  currency:     readLS('haythix.displayCurrency', 'USD'),
  numberFormat: readLS('haythix.numberFormat',    'auto'),
  timezone:     readLS('haythix.displayTimezone', 'auto'),
  rates:        { USD: 1 },
};

function readLS(key, fallback) {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
}

// ─── Subscriptions ────────────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'haythix.displayCurrency') state.currency     = e.newValue || 'USD';
    if (e.key === 'haythix.numberFormat')    state.numberFormat = e.newValue || 'auto';
    if (e.key === 'haythix.displayTimezone') state.timezone     = e.newValue || 'auto';
  });
  window.addEventListener('haythix:currency',      (e) => { state.currency     = e.detail || 'USD'; });
  window.addEventListener('haythix:number-format', (e) => { state.numberFormat = e.detail || 'auto'; });
  window.addEventListener('haythix:timezone',      (e) => { state.timezone     = e.detail || 'auto'; });
}

// Called by useCurrency hook after FX rates fetch
export function setRates(rates) {
  state.rates = { USD: 1, ...(rates || {}) };
}

export function getCurrency() { return state.currency; }
export function getTimezone() { return state.timezone; }
export function getNumberFormat() { return state.numberFormat; }
export function getRates() { return state.rates; }

// ─── USD → display-currency formatter ─────────────────────────────────────
export function formatUsd(usdAmount, dp = 2) {
  if (usdAmount === null || usdAmount === undefined || !Number.isFinite(Number(usdAmount))) return '—';
  const num = Number(usdAmount);
  const rate = state.rates[state.currency] || 1;
  const converted = num * rate;
  const sym = SYMBOLS[state.currency] || `${state.currency} `;
  const formatted = formatPlainNumber(converted, dp);
  return `${sym}${formatted}`;
}

export function convertUsd(usdAmount) {
  if (usdAmount === null || usdAmount === undefined) return null;
  const num = Number(usdAmount);
  if (!Number.isFinite(num)) return null;
  return num * (state.rates[state.currency] || 1);
}

// Compact currency formatter used in chart tick labels: returns "$1.2k" /
// "€1.2k" / "¥250M" etc — symbol matches the active display currency, value
// is converted via FX rate. Drop-in replacement for the hardcoded
// `tickFormatter={v => '$' + v}` patterns scattered across the chart pages.
export function formatUsdCompact(usdAmount) {
  if (usdAmount === null || usdAmount === undefined) return '';
  const num = Number(usdAmount);
  if (!Number.isFinite(num)) return '';
  const converted = num * (state.rates[state.currency] || 1);
  const sym = SYMBOLS[state.currency] || `${state.currency} `;
  const abs = Math.abs(converted);
  if (abs >= 1e12) return `${sym}${(converted / 1e12).toFixed(1)}T`;
  if (abs >= 1e9)  return `${sym}${(converted / 1e9).toFixed(1)}B`;
  if (abs >= 1e6)  return `${sym}${(converted / 1e6).toFixed(1)}M`;
  if (abs >= 1e3)  return `${sym}${(converted / 1e3).toFixed(1)}k`;
  return `${sym}${converted.toFixed(0)}`;
}

// ─── Number formatter ─────────────────────────────────────────────────────
export function formatNumber(n, dp = 0) {
  if (n === null || n === undefined) return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return formatPlainNumber(num, dp);
}

function formatPlainNumber(num, dp) {
  if (state.numberFormat === 'compact') {
    const abs = Math.abs(num);
    if (abs >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
    if (abs >= 1e9)  return `${(num / 1e9).toFixed(2)}B`;
    if (abs >= 1e6)  return `${(num / 1e6).toFixed(2)}M`;
    if (abs >= 1e3)  return `${(num / 1e3).toFixed(1)}K`;
    return num.toFixed(dp);
  }
  const locale = NUMBER_LOCALES[state.numberFormat];
  try {
    return num.toLocaleString(locale, { minimumFractionDigits: dp, maximumFractionDigits: dp });
  } catch {
    return num.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
  }
}

// ─── Date / time formatters ───────────────────────────────────────────────
function tzOpt() {
  return state.timezone === 'auto' ? undefined : { timeZone: state.timezone };
}

export function formatDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  try { return dt.toLocaleDateString(undefined, tzOpt()); }
  catch { return dt.toLocaleDateString(); }
}

export function formatTime(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  try { return dt.toLocaleTimeString(undefined, tzOpt()); }
  catch { return dt.toLocaleTimeString(); }
}

export function formatDateTime(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  try { return dt.toLocaleString(undefined, tzOpt()); }
  catch { return dt.toLocaleString(); }
}
